import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { datricApi } from '../../../services/api/datric.api';
import { CardQuote, Payment } from '../../../models/payment.model';
import { toastService } from '../../../services/toast.service';

export interface PaymentState {
	current: Payment | null;
	quote: CardQuote | null;
	loading: boolean;
	quoting: boolean;
}

const initialState: PaymentState = {
	current: null,
	quote: null,
	loading: false,
	quoting: false
};

export const createPix = createAsyncThunk('payment/createPix', async (amountBRL: number, { rejectWithValue }) => {
	try {
		return await datricApi.payment.createPix(amountBRL);
	} catch (e: any) {
		toastService.error(typeof e === 'string' ? e : 'Falha ao gerar PIX');
		return rejectWithValue(e);
	}
});

export const quoteCard = createAsyncThunk('payment/quoteCard', async (credits: number, { rejectWithValue }) => {
	try {
		return await datricApi.payment.quoteCard(credits);
	} catch (e: any) {
		return rejectWithValue(e);
	}
});

export const createCard = createAsyncThunk('payment/createCard', async (credits: number, { rejectWithValue }) => {
	try {
		return await datricApi.payment.createCard(credits);
	} catch (e: any) {
		toastService.error(typeof e === 'string' ? e : 'Falha ao gerar cobrança no cartão');
		return rejectWithValue(e);
	}
});

export const pollPayment = createAsyncThunk('payment/poll', async (id: string) => {
	return datricApi.payment.get(id);
});

export const paymentSlice = createSlice({
	name: 'payment',
	initialState,
	reducers: {
		clearPayment: state => {
			state.current = null;
		}
	},
	extraReducers: builder => {
		builder.addCase(createPix.pending, state => {
			state.loading = true;
		});
		builder.addCase(createPix.fulfilled, (state, action) => {
			state.loading = false;
			state.current = action.payload as Payment;
		});
		builder.addCase(createPix.rejected, state => {
			state.loading = false;
		});
		builder.addCase(createCard.pending, state => {
			state.loading = true;
		});
		builder.addCase(createCard.fulfilled, (state, action) => {
			state.loading = false;
			state.current = action.payload as Payment;
		});
		builder.addCase(createCard.rejected, state => {
			state.loading = false;
		});
		builder.addCase(quoteCard.pending, state => {
			state.quoting = true;
		});
		builder.addCase(quoteCard.fulfilled, (state, action) => {
			state.quoting = false;
			state.quote = action.payload as CardQuote;
		});
		builder.addCase(quoteCard.rejected, state => {
			state.quoting = false;
			state.quote = null;
		});
		builder.addCase(pollPayment.fulfilled, (state, action) => {
			state.current = action.payload as Payment;
		});
	}
});

export const { clearPayment } = paymentSlice.actions;
